import { useContext, useState } from "react";
import { useApi } from "../../../contexts/app/provider";
import {
  useGetMyRunComparisonSummaryQuery,
  useSetMyRunDescriptionMutation,
  useSetMyRunNameMutation,
} from "../../../services/api/__generated__/backend_gateway-types";
import { RunDetailsComponent } from "./component";
import { MetricDetails } from "./component";
import { Run } from "../../../services/perspective_api/__generated__/perspective_server-types";
import { SysMessagesContext } from "../../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../../contexts/sys_messages/type";
import { KPICollection, Alerts, TrendData } from "./types";

export const RunDetailsContainer = (props: { run: Run; previousRun: Run }) => {
  const { run, previousRun } = props;
  const { apiService } = useApi();
  const [currentYearRevenueDetails, setCurrentYearRevenueDetails] =
    useState<MetricDetails | null>(null);
  const [currentYearPurchasesDetails, setCurrentYearPurchasesDetails] =
    useState<MetricDetails | null>(null);
  const [maxInventoryOnHandDetails, setMaxInventoryOnHandDetails] =
    useState<MetricDetails | null>(null);
  const [summaryText, setSummaryText] = useState<string>(
    "No summary available"
  );

  // Enhanced statistics state
  const [kpis, setKpis] = useState<KPICollection | null>(null);
  const [alerts, setAlerts] = useState<Alerts | null>(null);
  const [trendData, setTrendData] = useState<TrendData | null>(null);

  const { loading } = useGetMyRunComparisonSummaryQuery({
    client: apiService.getClient(),
    variables: {
      runSettings1: {
        inputId: run.runSettings.inputId,
        runAttemptId: run.runSettings.runAttemptId,
        settingsNameAndVersion: run.runSettings.settingsNameAndVersion,
        workflowId: run.runSettings.workflowId,
      },
      runSettings2: {
        inputId: previousRun.runSettings.inputId,
        runAttemptId: previousRun.runSettings.runAttemptId,
        settingsNameAndVersion: previousRun.runSettings.settingsNameAndVersion,
        workflowId: previousRun.runSettings.workflowId,
      },
    },
    onCompleted: (data) => {
      const comparisonJson = data?.getMyRunComparisonSummary || "";
      const {
        comparison_result: comparisonResult,
        ai_summary: aiSummary,
        enhanced_statistics: enhancedStats,
      } = JSON.parse(comparisonJson);

      // Parse legacy metrics (keep for backward compatibility)
      const currentYearRevenue = comparisonResult?.sales.metrics.find(
        (m: any) => m.metric_name === "total_revenue"
      );
      const currentYearRevenueDetails = {
        current_value: currentYearRevenue?.current_value,
        percentage_change: currentYearRevenue?.percentage_change,
      };
      setCurrentYearRevenueDetails(currentYearRevenueDetails);

      const currentYearPurchases = comparisonResult?.purchases.metrics.find(
        (m: any) => m.metric_name === "total_cost"
      );
      const currentYearPurchasesDetails = {
        current_value: currentYearPurchases?.current_value,
        percentage_change: currentYearPurchases?.percentage_change,
      };
      setCurrentYearPurchasesDetails(currentYearPurchasesDetails);

      const maxInventoryOnHand = comparisonResult?.inventory.metrics.find(
        (m: any) => m.metric_name === "max_cost"
      );
      const maxInventoryOnHandDetails = {
        current_value: maxInventoryOnHand?.current_value,
        percentage_change: maxInventoryOnHand?.percentage_change,
      };
      setMaxInventoryOnHandDetails(maxInventoryOnHandDetails);

      const summaryText = aiSummary?.summary_text || "No summary available";
      setSummaryText(summaryText);

      // Parse enhanced statistics (new)
      if (enhancedStats) {
        setKpis(enhancedStats.kpis || null);
        setAlerts(enhancedStats.alerts || null);
        setTrendData(enhancedStats.trend_data || null);
      }
    },
  });

  const { addSystemMessage } = useContext(SysMessagesContext);

  const [setMyRunName] = useSetMyRunNameMutation({
    client: apiService.getClient(),
  });
  const setName = (name: string) => {
    setMyRunName({
      variables: {
        runSettings: {
          ...run.runSettings,
        },
        name,
      },
      onError: (error) => {
        if (error?.message !== undefined) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message: error.message!,
          });
        }
      },
      onCompleted: () => {
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: "Name updated",
        });
      },
    });
  };

  const [setMyRunDescription] = useSetMyRunDescriptionMutation({
    client: apiService.getClient(),
  });
  const setDescription = (description: string) => {
    setMyRunDescription({
      variables: {
        runSettings: {
          ...run.runSettings,
        },
        description,
      },
      onError: (error) => {
        if (error?.message !== undefined) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message: error.message!,
          });
        }
      },
      onCompleted: () => {
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: "Description updated",
        });
      },
    });
  };

  return (
    <RunDetailsComponent
      loading={loading}
      currentYearRevenueDetails={currentYearRevenueDetails}
      currentYearPurchasesDetails={currentYearPurchasesDetails}
      maxInventoryOnHandDetails={maxInventoryOnHandDetails}
      summaryText={summaryText}
      origName={run.name || null}
      saveName={setName}
      origDescription={run.description || null}
      saveDescription={setDescription}
      kpis={kpis}
      alerts={alerts}
      trendData={trendData}
    />
  );
};
